export type HighlightTextChunk = {
  text: string
  isHighlight: boolean
}

type SensitivityOptions = {
  caseSensitive: boolean
  accentSensitive: boolean
}

const formatSensitiveText = (text: string, options: SensitivityOptions): string => {
  let buffer = text

  if (!options.caseSensitive) {
    buffer = buffer.toLowerCase()
  }

  if (!options.accentSensitive) {
    buffer = buffer.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  return buffer
}

// Splits `text` into chunks, flagging the first occurrence of each search term with `isHighlight`
export const hashHighlightString = ({
  text,
  search = [],
  result = [],
  options = { caseSensitive: true, accentSensitive: true },
}: {
  text: string
  search: string[]
  result?: HighlightTextChunk[]
  options?: SensitivityOptions
}): HighlightTextChunk[] => {
  if (result.length === 0) {
    search = search.map((s) => formatSensitiveText(s, options))
    result = [{ text, isHighlight: false }]
  }

  const activeSearch = search.shift()

  if (activeSearch === undefined) {
    return result
  }

  // Searches the chunks already produced, not the original text, so a chunk can be split again by a
  // later term: search terms come in any order
  const relativeChunk = result.findIndex(
    ({ text, isHighlight }) =>
      formatSensitiveText(text, options).includes(activeSearch) && isHighlight === false,
  )

  // Term not found, skip to the next one; does not happen with the app's own call sites
  if (relativeChunk === -1) {
    return hashHighlightString({ text, search, result, options })
  }

  const activeText = result[relativeChunk]!.text
  const curatedActiveText = formatSensitiveText(activeText, options)

  const highlightBegin = curatedActiveText.indexOf(activeSearch)
  const highlightEnd = highlightBegin + activeSearch.length

  const beforeHighlightedText = activeText.substring(0, highlightBegin)
  const highlightedText = activeText.substring(highlightBegin, highlightEnd)
  const afterHighlightedText = activeText.substring(highlightEnd)

  const correctedText = [
    { text: beforeHighlightedText, isHighlight: false },
    { text: highlightedText, isHighlight: true },
    { text: afterHighlightedText, isHighlight: false },
  ].filter(({ text }) => text !== '')

  result.splice(relativeChunk, 1, ...correctedText)

  return hashHighlightString({ text, search, result, options })
}
